import { useCallback, useEffect, useState } from 'react';

import { promptsApi } from '../../api';
import {
  Button,
  CopyButton,
  Modal,
  Select,
  Switch,
  TextArea,
  TextInput,
} from '../../components/common';
import { usePrompts } from '../../context/PromptContext';
import { useToast } from '../../context/ToastContext';
import { useAttributeSets } from '../../hooks/useAttributeSet';
import { useModel } from '../../context/ModelContext';
import '../pages.css';

const BLANK = {
  name: '',
  description: '',
  body: '',
  is_extraction: false,
  attribute_set: '',
  is_default: false,
};

const RESERVED = [
  ['{{attribute_list}}', 'the numbered attribute list, built from the attribute set'],
  ['{{response_format}}', 'the response-format instructions for the version’s scheme'],
  ['{{attribute_count}}', 'how many attributes are in the set'],
  ['{{attribute_names}}', 'a comma-separated list of attribute names'],
];

/**
 * The prompt library.
 *
 * Prompts belong to the user: the app stores them, fills `{{variables}}`, and
 * otherwise leaves them alone. "Test render" shows the exact text that would be
 * sent — worth having, since an extraction prompt expands to roughly 14 kB.
 */
export function PromptsTab() {
  const toast = useToast();
  const { prompts, create, update, remove, refresh } = usePrompts();
  const { sets } = useAttributeSets();
  const { activeVersion } = useModel();

  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!selectedId) {
      setDraft(BLANK);
      return;
    }
    const prompt = prompts.find((candidate) => candidate.id === selectedId);
    if (prompt) {
      setDraft({
        name: prompt.name,
        description: prompt.description || '',
        body: prompt.body,
        is_extraction: prompt.is_extraction,
        attribute_set: prompt.attribute_set || '',
        is_default: prompt.is_default,
      });
    }
  }, [selectedId, prompts]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      if (selectedId) {
        await update(selectedId, draft);
        toast.success('Prompt saved', draft.name);
      } else {
        const created = await create(draft);
        setSelectedId(created.id);
        toast.success('Prompt created', created.name);
      }
    } catch (cause) {
      toast.error(cause, { title: 'Could not save the prompt' });
    } finally {
      setSaving(false);
    }
  }, [create, draft, selectedId, toast, update]);

  const testRender = useCallback(async () => {
    try {
      const variables = {};
      (draft.body.match(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g) || []).forEach((token) => {
        const name = token.replace(/[{}\s]/g, '');
        if (!RESERVED.some(([reserved]) => reserved.includes(name))) {
          variables[name] = `<${name}>`;
        }
      });
      const rendered = await promptsApi.render({
        body: draft.body,
        attribute_set: draft.attribute_set || undefined,
        version_id: draft.attribute_set ? undefined : activeVersion?.id,
        variables,
      });
      setPreview(rendered);
    } catch (cause) {
      toast.error(cause, { title: 'Could not render the prompt' });
    }
  }, [activeVersion?.id, draft.attribute_set, draft.body, toast]);

  const importFile = useCallback(
    async (file) => {
      try {
        const parsed = JSON.parse(await file.text());
        const list = Array.isArray(parsed) ? parsed : parsed.prompts;
        const { imported } = await promptsApi.import(list, { overwrite: false });
        await refresh();
        toast.success('Prompts imported', `${imported} added`);
      } catch (cause) {
        toast.error(cause, { title: 'Import failed' });
      }
    },
    [refresh, toast],
  );

  return (
    <div className="settings">
      <div className="col" style={{ gap: 10 }}>
        <div className="list-panel">
          {prompts.map((prompt) => (
            <button
              key={prompt.id}
              type="button"
              className={`list-item ${prompt.id === selectedId ? 'list-item--active' : ''}`}
              onClick={() => setSelectedId(prompt.id)}
            >
              <div className="list-item__title">
                {prompt.name}
                {prompt.is_extraction && <span className="xs subtle">▤</span>}
              </div>
              <div className="list-item__meta">
                {prompt.is_default && 'default · '}
                {prompt.is_extraction ? 'extraction' : 'free chat'}
                {prompt.variables?.length ? ` · ${prompt.variables.length} variable(s)` : ''}
                {prompt.use_count ? ` · used ${prompt.use_count}×` : ''}
              </div>
            </button>
          ))}
          <button
            type="button"
            className="list-item"
            style={{ color: 'var(--fg-subtle)' }}
            onClick={() => {
              setSelectedId(null);
              setDraft(BLANK);
            }}
          >
            ＋ New prompt
          </button>
        </div>

        <div className="row">
          <Button size="sm" variant="ghost" onClick={() => window.open(promptsApi.exportUrl())}>
            Export all
          </Button>
          <label className="btn btn--ghost btn--sm">
            Import
            <input
              type="file"
              accept="application/json"
              className="sr-only"
              onChange={(event) => {
                if (event.target.files?.[0]) importFile(event.target.files[0]);
                event.target.value = '';
              }}
            />
          </label>
        </div>
      </div>

      <section className="card card--pad editor">
        <div className="editor__row">
          <TextInput
            label="NAME"
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            placeholder="School zone focus"
          />
          <TextInput
            label="DESCRIPTION"
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            placeholder="What this prompt is for"
          />
        </div>

        <TextArea
          label="PROMPT BODY"
          mono
          value={draft.body}
          onChange={(event) => setDraft({ ...draft, body: event.target.value })}
          placeholder={
            'Ask anything, or compose an extraction prompt:\n\n{{attribute_list}}\n\n{{response_format}}'
          }
          style={{ minHeight: 240 }}
          hint="Any other {{name}} becomes a variable you fill in per request."
        />

        <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
          {RESERVED.map(([token, meaning]) => (
            <button
              key={token}
              type="button"
              className="chip chip--button"
              title={meaning}
              onClick={() =>
                setDraft({ ...draft, body: `${draft.body}${draft.body ? '\n\n' : ''}${token}` })
              }
            >
              {token}
            </button>
          ))}
        </div>

        <div className="editor__row">
          <Select
            label="ATTRIBUTE SET"
            value={draft.attribute_set}
            onChange={(event) => setDraft({ ...draft, attribute_set: event.target.value })}
            placeholder="Follow the selected version"
            options={sets.map((set) => ({
              value: set.id,
              label: `${set.label} (${set.attribute_count})`,
            }))}
            hint="Only used when the prompt does attribute extraction."
          />
          <div className="col" style={{ gap: 10, justifyContent: 'center' }}>
            <Switch
              checked={draft.is_extraction}
              onChange={(value) => setDraft({ ...draft, is_extraction: value })}
              label="Attribute extraction (parse the response)"
            />
            <Switch
              checked={draft.is_default}
              onChange={(value) => setDraft({ ...draft, is_default: value })}
              label="Use as the default prompt"
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: 4 }}>
          <Button
            variant="primary"
            onClick={save}
            loading={saving}
            disabled={!draft.name || !draft.body}
          >
            {selectedId ? 'Save prompt' : 'Create prompt'}
          </Button>
          <Button variant="ghost" onClick={testRender} disabled={!draft.body}>
            Test render
          </Button>
          {selectedId && (
            <Button
              variant="danger"
              style={{ marginLeft: 'auto' }}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          )}
        </div>
      </section>

      <Modal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title="Rendered prompt"
        subtitle={
          preview
            ? `${preview.characters.toLocaleString()} characters · ${preview.lines} lines · ~${preview.estimated_tokens.toLocaleString()} tokens${
                preview.attribute_count ? ` · ${preview.attribute_count} attributes` : ''
              }`
            : ''
        }
        wide
        footer={preview && <CopyButton value={preview.rendered} label="Copy" size="md" />}
      >
        <pre
          className="mono"
          style={{
            whiteSpace: 'pre-wrap',
            margin: 0,
            maxHeight: '60vh',
            overflowY: 'auto',
            color: 'var(--fg)',
          }}
        >
          {preview?.rendered}
        </pre>
      </Modal>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete “${draft.name}”?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await remove(selectedId);
                setSelectedId(null);
                setConfirmDelete(false);
                toast.info('Prompt deleted');
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        Conversations that used this prompt keep the text that was actually sent, so history is not
        affected.
      </Modal>
    </div>
  );
}
